import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CalendarDays,
  EyeOff,
  Link2,
  MessageSquareText,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { connectionsAPI, userProfilesAPI } from "../services/api";
import { getConnectionLimits } from "../constants/contentLimits";
import { useAuthState } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import "./PublicProfile.css";

const UserPublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { user, token } = useAuthState();
  const connectionLimits = getConnectionLimits(user);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [noteSending, setNoteSending] = useState(false);
  const [noteStatus, setNoteStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const currentUserId = String(user?._id || user?.id || "");

  useEffect(() => {
    const loadProfile = async () => {
      setConnectStatus(null);
      setNoteStatus(null);
      try {
        const response = await userProfilesAPI.getPublicProfile(userId);
        const nextProfile = response.data.publicProfile;
        setProfile(nextProfile);
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Public profile not found",
          "error-warning",
        );
        navigate("/communities");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [addToast, navigate, userId]);

  useEffect(() => {
    const loadConnectionStatus = async () => {
      if (!token || !profile?.userId || String(profile.userId) === currentUserId) {
        return;
      }

      try {
        const statusResponse = await connectionsAPI.getTargetStatus(
          profile.userId,
        );
        setNoteStatus(statusResponse.data);
        setConnectStatus(statusResponse.data.connectStatus || "none");
      } catch {
        setConnectStatus("none");
      }
    };

    loadConnectionStatus();
  }, [currentUserId, profile?.userId, token]);

  if (loading) return <Spinner visible={true} />;

  const memberSince = profile?.memberSince
    ? new Date(profile.memberSince).toLocaleDateString()
    : null;
  const canSendNote =
    token &&
    profile?.userId &&
    String(profile.userId) !== currentUserId &&
    !profile?.isDeactivated;
  const canShowConnectButton =
    canSendNote &&
    connectStatus &&
    connectStatus !== "accepted" &&
    connectStatus !== "inactive";
  const noteBodyLimit =
    noteStatus?.noteBodyLimit || connectionLimits.connectionNoteBody;
  const dailyNoteLimit =
    noteStatus?.dailyLimit || connectionLimits.connectionDailyNotes;
  const dailyNotesUsed = noteStatus?.dailyUsage?.mine || 0;
  const dailyLimitReached =
    noteStatus ? !noteStatus.canSend : dailyNotesUsed >= dailyNoteLimit;
  const noteDisabledReason =
    noteStatus?.disabledReason ||
    (profile?.isDeactivated
      ? "This member has self-deactivated their account. Notes are paused until they reactivate."
      : "");

  const openNoteModal = async () => {
    setNoteOpen(true);
    if (!profile?.userId) return;

    try {
      const response = await connectionsAPI.getTargetStatus(profile.userId);
      setNoteStatus(response.data);
      setConnectStatus(response.data.connectStatus || "none");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Could not retrieve note allowance",
        "error-warning",
      );
    }
  };

  const handleSendNote = async () => {
    const trimmed = noteBody.trim();
    if (!trimmed) {
      addToast("Note is required", "error-warning");
      return;
    }

    try {
      setNoteSending(true);
      const response = await connectionsAPI.startConnection({
        targetUserId: profile.userId,
        body: trimmed,
        contextType: "profile",
      });
      setNoteStatus((prev) => ({
        ...(prev || {}),
        dailyLimit: response.data.noteLimit || dailyNoteLimit,
        noteBodyLimit,
        dailyUsage: {
          mine: response.data.dailyUsage?.mine || dailyNotesUsed + 1,
        },
        connectStatus: response.data.connectStatus || prev?.connectStatus,
        relationStatus: response.data.relationStatus || prev?.relationStatus,
        canSend:
          (response.data.dailyUsage?.mine || dailyNotesUsed + 1) <
          (response.data.noteLimit || dailyNoteLimit),
      }));
      addToast("Note sent", "success-info");
      setNoteOpen(false);
      setNoteBody("");
      window.dispatchEvent(new Event("inbox-changed"));
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to send note",
        "error-warning",
      );
    } finally {
      setNoteSending(false);
    }
  };

  return (
    <main className="public-profile-page public-profile-view-page">
      <div className="public-profile-header">
        <div className="public-profile-title-block">
          <div className="public-profile-title-row">
            <h1>{profile?.publicDisplayName || "Asasiasa member"}</h1>
            {canSendNote ? (
              <Button
                className="public-profile-note-button"
                onClick={openNoteModal}
                title="Send note"
                aria-label="Send note"
              >
                <MessageSquareText size={14} />
              </Button>
            ) : null}
          </div>
          <p>Public Asasiasa profile.</p>
        </div>
        {canShowConnectButton && (
          <Button
            className="button-btn button-btn-blue public-profile-back-button"
            onClick={async () => {
              if (connectLoading) return;
              setConnectLoading(true);
              try {
                const response = await connectionsAPI.sendConnectRequest(profile.userId);
                setConnectStatus(response.data?.connectStatus || "pending_sent");
                addToast("Connect request sent", "success-info");
              } catch (error) {
                const msg = error.response?.data?.details?.[0] || error.response?.data?.error;
                if (msg === "A connect request is already pending") {
                  setConnectStatus("pending_sent");
                }
                if (msg === "You are already connected") {
                  setConnectStatus("accepted");
                }
                addToast(msg || "Failed to send request", "error-warning");
              } finally {
                setConnectLoading(false);
              }
            }}
            disabled={
              connectLoading ||
              connectStatus === "pending_sent" ||
              connectStatus === "pending_received"
            }
          >
            <Link2 size={16} />
            {connectStatus === "pending_sent"
              ? "Request sent"
              : connectStatus === "pending_received"
                ? "Request received"
                : "Connect"}
          </Button>
        )}
      </div>

      <section className="public-profile-public-card">
        <div className="public-profile-preview-avatar public-profile-public-avatar">
          {(profile?.publicDisplayName || "M").trim().charAt(0).toUpperCase()}
        </div>
        <div className="public-profile-public-copy">
          <h2>{profile?.publicDisplayName || "Asasiasa member"}</h2>
          {profile?.isDeactivated ? (
            <div className="public-profile-private-note">
              <EyeOff size={18} />
              <span>
                {profile?.isSelfDeactivated
                  ? "This member has self-deactivated their account."
                  : "This profile is currently unavailable."}
              </span>
            </div>
          ) : profile?.publicProfileVisible === false ? (
            <div className="public-profile-private-note">
              <EyeOff size={18} />
              <span>This profile is private.</span>
            </div>
          ) : (
            <>
              <p>{profile?.publicBio || "No public bio yet."}</p>
              {profile?.publicInterests ? (
                <div className="public-profile-public-meta">
                  <Sparkles size={16} />
                  <span>{profile.publicInterests}</span>
                </div>
              ) : null}
            </>
          )}
          {memberSince ? (
            <div className="public-profile-public-meta">
              <CalendarDays size={16} />
              <span>Member since {memberSince}</span>
            </div>
          ) : null}
        </div>
      </section>

      {noteOpen ? (
        <div
          className="public-profile-note-overlay"
          onClick={() => setNoteOpen(false)}
        >
          <div
            className="public-profile-note-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="public-profile-note-header">
              <div>
                <h2>Send note</h2>
                <p>To {profile?.publicDisplayName || "Asasiasa member"}</p>
              </div>
              <Button
                className="public-profile-note-close"
                onClick={() => setNoteOpen(false)}
                title="Close"
                aria-label="Close"
              >
                <X size={15} />
              </Button>
            </div>
            <textarea
              value={noteBody}
              onChange={(event) => setNoteBody(event.target.value)}
              placeholder="Write a short note..."
              maxLength={noteBodyLimit}
              rows="5"
              className="public-profile-note-textarea"
              disabled={dailyLimitReached}
            />
            <div className="public-profile-note-actions">
              <span className={dailyLimitReached ? "is-limit-reached" : ""}>
                {noteDisabledReason ||
                  `${noteBody.length}/${noteBodyLimit} · ${dailyNotesUsed}/${dailyNoteLimit} today`}
              </span>
              <Button
                className="button-btn button-btn-blue"
                onClick={handleSendNote}
                disabled={noteSending || dailyLimitReached}
              >
                <Send size={16} />
                Send note
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default UserPublicProfile;
